import { useContext, useMemo, useState } from 'react';
import * as R from 'ramda';

import { useNavigation } from '@atom/hooks/useNavigation';
import { useUserProfile } from '@atom/hooks/useUserProfile';
import { Button, Icon, IconButton, Modal, Skeleton, Tooltip } from '@atom/mui';
import { DashboardCard } from '@atom/types/dashboard';
import {
  isNilOrEmpty,
  isValidUrlWithMessage,
} from '@atom/utilities/validationUtilities';

import CustomEmbedFormView from './CustomizeDashboardModal/CustomEmbedFormView';
import DashboardContext from './DashboardContext';

import classes from './dashboardCard.module.css';

const MAX_CHAR_WIDTH_SM = 30;
const MAX_CHAR_WIDTH_LG = 90;
const TITLE_LOAD_SKELETON_WIDTH = '10rem';

interface Props {
  card: DashboardCard;
}

const blankIcon = (
  <div key="blank-icon" style={{ visibility: 'hidden' }}>
    <IconButton>
      <Icon>blank</Icon>
    </IconButton>
  </div>
);

const DashboardCardTitlebar = ({ card }: Props) => {
  const [, { Link }] = useNavigation();
  const { userId } = useUserProfile();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [urlErrorMsg, setUrlErrorMsg] = useState('');
  const { handleDeleteEmbedCard, handleEditEmbedCard, loadingEmbedCard } =
    useContext(DashboardContext);
  const [cardData, setCardData] = useState(card);

  const isLoading = card.contentState === 'LOADING';
  const isEditable = card.type === 'EMBED';
  const charThreshold =
    card.size === 'SM' ? MAX_CHAR_WIDTH_SM : MAX_CHAR_WIDTH_LG;
  const overflowing = card.title.length > charThreshold;
  const hasPortalLink = !isNilOrEmpty(card.portalLink);

  const closeAndEditCard = () => {
    setOpenEditModal(false);
    handleEditEmbedCard(cardData);
  };

  const portalLinkIcon = hasPortalLink ? (
    <div
      key="portal-link-icon"
      className={classes.cardActionIcon}
      data-cy={`dashboardCardLinkIcon_${card.title}`}
    >
      <IconButton tooltip="Open in Portal">
        <Link to={card.portalLink(userId)} target="_blank">
          <Icon>open_in_new</Icon>
        </Link>
      </IconButton>
    </div>
  ) : null;

  const editIcon = isEditable ? (
    <div
      key="edit-card-icon"
      className={classes.cardActionIcon}
      onClick={() => setOpenEditModal(true)}
      data-cy={`dashboardCardEditIcon_${card.title}`}
    >
      <IconButton tooltip="Edit Card">
        <Icon>edit</Icon>
      </IconButton>
    </div>
  ) : (
    <div />
  );

  const deleteIcon = isEditable ? (
    <div
      key="delete-card-icon"
      className={classes.cardActionIcon}
      onClick={() => setOpenDeleteModal(true)}
      data-cy={`dashboardCardDeleteIcon_${card.title}`}
    >
      <IconButton tooltip="Delete Card">
        <Icon>delete</Icon>
      </IconButton>
    </div>
  ) : (
    <div />
  );

  const getDeleteCardFooter = () => (
    <div>
      <Button
        onClick={() => setOpenDeleteModal(false)}
        style={{ marginRight: '8px' }}
      >
        CANCEL
      </Button>
      <Button
        color="error"
        variant="contained"
        onClick={() => handleDeleteEmbedCard(card)}
      >
        DELETE
      </Button>
    </div>
  );

  const editFormIsValid = useMemo(() => {
    const [isURLValid, urlError] = isValidUrlWithMessage(cardData.embedLink);
    setUrlErrorMsg(urlError);
    const isHyperlinkValid = !R.isEmpty(cardData.embedLink) && isURLValid;

    return isHyperlinkValid && !R.isEmpty(cardData.title);
  }, [cardData]);

  const cardActionIcons =
    !isLoading && (isEditable || hasPortalLink)
      ? [
          ...(deleteIcon ? [deleteIcon] : []),
          ...(editIcon ? [editIcon] : []),
          ...(portalLinkIcon ? [portalLinkIcon] : []),
        ]
      : [blankIcon];

  return (
    <>
      <div className={classes.cardTitlebar}>
        <Tooltip title={overflowing ? card.title : ''}>
          <div className={classes.cardTitle}>
            {isLoading ? (
              <Skeleton width={TITLE_LOAD_SKELETON_WIDTH} />
            ) : (
              card.title
            )}
          </div>
        </Tooltip>
        <div className={classes.cardTitleIcons}>
          {cardActionIcons.map(icon => icon)}
        </div>
      </div>
      <Modal
        title="Edit Card: Custom Embed"
        open={openEditModal}
        onCancel={() => setOpenEditModal(false)}
        onExited={() => setOpenEditModal(false)}
        onConfirm={closeAndEditCard}
        disabled={!editFormIsValid || loadingEmbedCard}
        data-cy={`dashboardCardEditModal_${card.title}`}
        confirmButtonText="SAVE"
      >
        <CustomEmbedFormView
          cardData={cardData}
          setCardData={setCardData}
          urlErrorMsg={urlErrorMsg}
        />
      </Modal>
      <Modal
        title="Delete Card?"
        open={openDeleteModal}
        onCancel={() => setOpenDeleteModal(false)}
        onExited={() => setOpenDeleteModal(false)}
        onConfirm={() => handleDeleteEmbedCard(card)}
        disabled={loadingEmbedCard}
        data-cy={`dashboardCardDeleteModal_${card.title}`}
        width="sm"
        footer={getDeleteCardFooter()}
      >
        Are you sure you want to delete this card?
      </Modal>
    </>
  );
};

export default DashboardCardTitlebar;
